import { useState } from 'react';
import {
  Box,
  Button,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Typography,
  IconButton,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  Alert,
  CircularProgress,
  Tooltip,
  Chip,
  FormControlLabel,
  Switch,
  Grid,
  Divider,
  Snackbar,
} from '@mui/material';
import {
  Add as AddIcon,
  Edit as EditIcon,
  Delete as DeleteIcon,
  LocationOn as LocationIcon,
  CheckCircle as CheckCircleIcon,
  Cancel as CancelIcon,
  Visibility as VisibilityIcon,
  Message as MessageIcon,
  Send as SendIcon,
  NotificationsOff as NoTokenIcon,
  Home as HomeIcon,
  DeleteOutline as DeleteOutlineIcon,
} from '@mui/icons-material';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { contribuyentesAPI, movilDireccionesAPI } from '../services/api';
import MapLocationPicker from '../components/MapLocationPicker';

const Contribuyentes = () => {
  const queryClient = useQueryClient();
  const [openDialog, setOpenDialog] = useState(false);
  const [editingContribuyente, setEditingContribuyente] = useState(null);
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
  const [contribuyenteToDelete, setContribuyenteToDelete] = useState(null);
  const [deleteInfo, setDeleteInfo] = useState(null);
  const [error, setError] = useState(null);
  const [mapPickerOpen, setMapPickerOpen] = useState(false);
  const [recoleccionesDialogOpen, setRecoleccionesDialogOpen] = useState(false);
  const [selectedContribuyente, setSelectedContribuyente] = useState(null);
  const [mensajesDialogOpen, setMensajesDialogOpen] = useState(false);
  const [mensajeContribuyente, setMensajeContribuyente] = useState(null);
  const [mensajeForm, setMensajeForm] = useState({ titulo: '', mensaje: '' });
  const [snackbar, setSnackbar] = useState({ open: false, message: '', severity: 'success' });

  // Estado para gestión de direcciones
  const [direccionesDialogOpen, setDireccionesDialogOpen] = useState(false);
  const [direccionesContribuyente, setDireccionesContribuyente] = useState(null);
  const [addDireccionDialogOpen, setAddDireccionDialogOpen] = useState(false);
  const [addDireccionForm, setAddDireccionForm] = useState({ alias: '', identificador: '', latitud: '', longitud: '' });
  const [addDireccionErrors, setAddDireccionErrors] = useState({});
  const [mapPickerForDireccionOpen, setMapPickerForDireccionOpen] = useState(false);
  const [editDireccionDialogOpen, setEditDireccionDialogOpen] = useState(false);
  const [editingDireccion, setEditingDireccion] = useState(null);
  const [editDireccionForm, setEditDireccionForm] = useState({ alias: '', identificador: '', latitud: '', longitud: '' });
  const [editDireccionErrors, setEditDireccionErrors] = useState({});
  const [mapPickerForEditOpen, setMapPickerForEditOpen] = useState(false);
  const [viewMapDireccionOpen, setViewMapDireccionOpen] = useState(false);
  const [viewMapDireccion, setViewMapDireccion] = useState(null);
  const [formData, setFormData] = useState({
    Nombre: '',
    Email: '',
    Phone: '',
    Identificador: '',
    Latitud: '',
    Longitud: '',
    Activo: 1,
  });

  // Query para obtener contribuyentes
  const {
    data: contribuyentesData,
    isLoading,
    isError,
    error: queryError,
  } = useQuery({
    queryKey: ['contribuyentes'],
    queryFn: async () => {
      const response = await contribuyentesAPI.listar();
      return response.data;
    },
  });

  // Mutation para crear contribuyente
  const createMutation = useMutation({
    mutationFn: contribuyentesAPI.crear,
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['contribuyentes'] });
      handleCloseDialog();
      setError(null);
    },
    onError: (error) => {
      setError(error.response?.data?.error?.message || 'Error al crear el contribuyente');
    },
  });

  // Mutation para actualizar contribuyente
  const updateMutation = useMutation({
    mutationFn: ({ id, data }) => contribuyentesAPI.actualizar(id, data),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['contribuyentes'] });
      handleCloseDialog();
      setError(null);
    },
    onError: (error) => {
      setError(error.response?.data?.error?.message || 'Error al actualizar el contribuyente');
    },
  });

  // Mutation para eliminar contribuyente
  const deleteMutation = useMutation({
    mutationFn: contribuyentesAPI.eliminar,
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['contribuyentes'] });
      setDeleteDialogOpen(false);
      setContribuyenteToDelete(null);
      setDeleteInfo(null);
    },
    onError: (error) => {
      setError(error.response?.data?.error?.message || 'Error al eliminar el contribuyente');
    },
  });

  // Query para obtener recolecciones de un contribuyente
  const {
    data: recoleccionesData,
    isLoading: recoleccionesLoading,
  } = useQuery({
    queryKey: ['recolecciones-contribuyente', selectedContribuyente?.Id],
    queryFn: async () => {
      if (!selectedContribuyente) return { data: [] };
      const response = await contribuyentesAPI.obtenerRecolecciones(selectedContribuyente.Id);
      return response.data;
    },
    enabled: !!selectedContribuyente && recoleccionesDialogOpen,
  });

  // Query para mensajes de un contribuyente
  const {
    data: mensajesData,
    isLoading: mensajesLoading,
    refetch: refetchMensajes,
  } = useQuery({
    queryKey: ['mensajes-contribuyente', mensajeContribuyente?.Id],
    queryFn: async () => {
      if (!mensajeContribuyente) return { data: [], tieneToken: false };
      const response = await contribuyentesAPI.obtenerMensajes(mensajeContribuyente.Id);
      return response.data;
    },
    enabled: !!mensajeContribuyente && mensajesDialogOpen,
  });

  // Mutation para enviar mensaje
  const enviarMensajeMutation = useMutation({
    mutationFn: ({ id, data }) => contribuyentesAPI.enviarMensaje(id, data),
    onSuccess: (response) => {
      refetchMensajes();
      setMensajeForm({ titulo: '', mensaje: '' });
      const sinToken = response.data?.sinToken;
      setSnackbar({
        open: true,
        message: sinToken
          ? 'Mensaje guardado (el contribuyente no tiene dispositivo registrado)'
          : 'Mensaje enviado exitosamente',
        severity: sinToken ? 'warning' : 'success',
      });
    },
    onError: (error) => {
      setSnackbar({
        open: true,
        message: error.response?.data?.error?.message || 'Error al enviar el mensaje',
        severity: 'error',
      });
    },
  });

  // Query para listar direcciones de un contribuyente
  const {
    data: direccionesData,
    isLoading: direccionesLoading,
    refetch: refetchDirecciones,
  } = useQuery({
    queryKey: ['direcciones-contribuyente', direccionesContribuyente?.Email],
    queryFn: async () => {
      if (!direccionesContribuyente?.Email) return { data: [] };
      const response = await movilDireccionesAPI.listarPorEmail(direccionesContribuyente.Email);
      return response.data;
    },
    enabled: !!direccionesContribuyente && direccionesDialogOpen,
  });

  // Mutation para agregar dirección
  const agregarDireccionMutation = useMutation({
    mutationFn: movilDireccionesAPI.agregar,
    onSuccess: () => {
      refetchDirecciones();
      setAddDireccionDialogOpen(false);
      setAddDireccionForm({ alias: '', identificador: '', latitud: '', longitud: '' });
      setAddDireccionErrors({});
      setSnackbar({ open: true, message: 'Dirección agregada', severity: 'success' });
    },
    onError: (err) => {
      setSnackbar({
        open: true,
        message: err.response?.data?.error?.message || 'Error al agregar la dirección',
        severity: 'error',
      });
    },
  });

  // Mutation para desactivar dirección
  const desactivarDireccionMutation = useMutation({
    mutationFn: movilDireccionesAPI.desactivar,
    onSuccess: () => {
      refetchDirecciones();
      setSnackbar({ open: true, message: 'Dirección eliminada', severity: 'success' });
    },
    onError: (err) => {
      setSnackbar({
        open: true,
        message: err.response?.data?.error?.message || 'Error al eliminar la dirección',
        severity: 'error',
      });
    },
  });

  // Mutation para actualizar dirección
  const actualizarDireccionMutation = useMutation({
    mutationFn: ({ id, data }) => movilDireccionesAPI.actualizar(id, data),
    onSuccess: () => {
      refetchDirecciones();
      setEditDireccionDialogOpen(false);
      setEditingDireccion(null);
      setSnackbar({ open: true, message: 'Dirección actualizada', severity: 'success' });
    },
    onError: (err) => {
      setSnackbar({
        open: true,
        message: err.response?.data?.error?.message || 'Error al actualizar la dirección',
        severity: 'error',
      });
    },
  });

  const handleOpenDireccionesDialog = (contribuyente) => {
    setDireccionesContribuyente(contribuyente);
    setDireccionesDialogOpen(true);
  };

  const handleCloseDireccionesDialog = () => {
    setDireccionesDialogOpen(false);
    setDireccionesContribuyente(null);
  };

  const handleOpenAddDireccion = () => {
    setAddDireccionForm({ alias: '', identificador: '', latitud: '', longitud: '' });
    setAddDireccionErrors({});
    setAddDireccionDialogOpen(true);
  };

  const handleCloseAddDireccion = () => {
    setAddDireccionDialogOpen(false);
    setAddDireccionForm({ alias: '', identificador: '', latitud: '', longitud: '' });
    setAddDireccionErrors({});
  };

  const handleOpenEditDireccion = (dir) => {
    setEditingDireccion(dir);
    setEditDireccionForm({
      alias: dir.Alias || '',
      identificador: dir.Identificador || '',
      latitud: dir.Latitud,
      longitud: dir.Longitud,
    });
    setEditDireccionErrors({});
    setEditDireccionDialogOpen(true);
  };

  const handleCloseEditDireccion = () => {
    setEditDireccionDialogOpen(false);
    setEditingDireccion(null);
    setEditDireccionErrors({});
  };

  const handleSubmitEditDireccion = () => {
    const errors = {};
    if (!editDireccionForm.alias.trim()) errors.alias = 'El alias es requerido';
    if (!editDireccionForm.latitud) errors.latitud = 'La latitud es requerida';
    if (!editDireccionForm.longitud) errors.longitud = 'La longitud es requerida';
    if (Object.keys(errors).length) {
      setEditDireccionErrors(errors);
      return;
    }
    actualizarDireccionMutation.mutate({
      id: editingDireccion.Id,
      data: {
        email: direccionesContribuyente.Email,
        alias: editDireccionForm.alias.trim(),
        identificador: editDireccionForm.identificador.trim() || null,
        latitud: parseFloat(editDireccionForm.latitud),
        longitud: parseFloat(editDireccionForm.longitud),
      },
    });
  };

  const handleSubmitDireccion = () => {
    const errors = {};
    if (!addDireccionForm.alias.trim()) errors.alias = 'El alias es requerido';
    if (!addDireccionForm.latitud) errors.latitud = 'La latitud es requerida';
    if (!addDireccionForm.longitud) errors.longitud = 'La longitud es requerida';
    if (Object.keys(errors).length) {
      setAddDireccionErrors(errors);
      return;
    }
    agregarDireccionMutation.mutate({
      email: direccionesContribuyente.Email,
      alias: addDireccionForm.alias.trim(),
      identificador: addDireccionForm.identificador.trim() || null,
      latitud: parseFloat(addDireccionForm.latitud),
      longitud: parseFloat(addDireccionForm.longitud),
    });
  };

  const handleOpenDialog = (contribuyente = null) => {
    if (contribuyente) {
      setEditingContribuyente(contribuyente);
      setFormData({
        Nombre: contribuyente.Nombre || '',
        Email: contribuyente.Email || '',
        Phone: contribuyente.Phone || '',
        Identificador: contribuyente.Identificador || '',
        Latitud: contribuyente.Latitud || '',
        Longitud: contribuyente.Longitud || '',
        Activo: contribuyente.Activo || 1,
      });
    } else {
      setEditingContribuyente(null);
      setFormData({
        Nombre: '',
        Email: '',
        Phone: '',
        Identificador: '',
        Latitud: '',
        Longitud: '',
        Activo: 1,
      });
    }
    setError(null);
    setOpenDialog(true);
  };

  const handleCloseDialog = () => {
    setOpenDialog(false);
    setEditingContribuyente(null);
    setError(null);
  };

  const handleChange = (field, value) => {
    setFormData((prev) => ({ ...prev, [field]: value }));
  };

  const validateForm = () => {
    if (!formData.Nombre || formData.Nombre.trim() === '') {
      setError('El nombre es obligatorio');
      return false;
    }
    if (formData.Email && !/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(formData.Email)) {
      setError('El email no es válido');
      return false;
    }
    return true;
  };

  const handleSubmit = () => {
    if (!validateForm()) return;

    const dataToSend = {
      Nombre: formData.Nombre.trim(),
      Email: formData.Email?.trim() || null,
      Phone: formData.Phone?.trim() || null,
      Identificador: formData.Identificador?.trim() || null,
      Latitud: formData.Latitud ? parseFloat(formData.Latitud) : null,
      Longitud: formData.Longitud ? parseFloat(formData.Longitud) : null,
      Activo: formData.Activo ? 1 : 0,
    };

    if (editingContribuyente) {
      updateMutation.mutate({ id: editingContribuyente.Id, data: dataToSend });
    } else {
      createMutation.mutate(dataToSend);
    }
  };

  const handleDeleteClick = async (contribuyente) => {
    setContribuyenteToDelete(contribuyente);
    try {
      const response = await contribuyentesAPI.verificarEliminacion(contribuyente.Id);
      setDeleteInfo(response.data.data);
      setDeleteDialogOpen(true);
    } catch (error) {
      setError(error.response?.data?.error?.message || 'Error al verificar el contribuyente');
    }
  };

  const handleConfirmDelete = () => {
    if (contribuyenteToDelete) {
      deleteMutation.mutate(contribuyenteToDelete.Id);
    }
  };

  const handleCloseDeleteDialog = () => {
    setDeleteDialogOpen(false);
    setContribuyenteToDelete(null);
    setDeleteInfo(null);
  };

  const handleOpenMapPicker = () => {
    setMapPickerOpen(true);
  };

  const handleCloseMapPicker = () => {
    setMapPickerOpen(false);
  };

  const handleLocationSelected = (location) => {
    handleChange('Latitud', location.lat);
    handleChange('Longitud', location.lng);
    handleCloseMapPicker();
  };

  const handleOpenMensajesDialog = (contribuyente) => {
    setMensajeContribuyente(contribuyente);
    setMensajeForm({ titulo: '', mensaje: '' });
    setMensajesDialogOpen(true);
  };

  const handleCloseMensajesDialog = () => {
    setMensajesDialogOpen(false);
    setMensajeContribuyente(null);
  };

  const handleEnviarMensaje = () => {
    if (!mensajeForm.titulo.trim() || !mensajeForm.mensaje.trim()) {
      setSnackbar({ open: true, message: 'Título y mensaje son requeridos', severity: 'warning' });
      return;
    }
    enviarMensajeMutation.mutate({
      id: mensajeContribuyente.Id,
      data: { titulo: mensajeForm.titulo.trim(), mensaje: mensajeForm.mensaje.trim() },
    });
  };

  const handleOpenRecoleccionesDialog = (contribuyente) => {
    setSelectedContribuyente(contribuyente);
    setRecoleccionesDialogOpen(true);
  };

  const handleCloseRecoleccionesDialog = () => {
    setRecoleccionesDialogOpen(false);
    setSelectedContribuyente(null);
  };

  const formatFecha = (fechaString) => {
    if (!fechaString) return '-';
    const fecha = new Date(fechaString);
    return fecha.toLocaleDateString('es-AR', {
      year: 'numeric',
      month: '2-digit',
      day: '2-digit',
      hour: '2-digit',
      minute: '2-digit'
    });
  };

  if (isLoading) {
    return (
      <Box display="flex" justifyContent="center" alignItems="center" minHeight="400px">
        <CircularProgress />
      </Box>
    );
  }

  if (isError) {
    return (
      <Box p={3}>
        <Alert severity="error">
          Error al cargar los contribuyentes: {queryError?.message || 'Error desconocido'}
        </Alert>
      </Box>
    );
  }

  const contribuyentes = contribuyentesData?.data || [];

  return (
    <Box p={3}>
      <Box display="flex" justifyContent="space-between" alignItems="center" mb={3}>
        <Typography variant="h4" component="h1">
          Contribuyentes
        </Typography>
        <Button
          variant="contained"
          color="primary"
          startIcon={<AddIcon />}
          onClick={() => handleOpenDialog()}
        >
          Nuevo Contribuyente
        </Button>
      </Box>

      {error && (
        <Alert severity="error" sx={{ mb: 2 }} onClose={() => setError(null)}>
          {error}
        </Alert>
      )}

      <TableContainer component={Paper}>
        <Table>
          <TableHead>
            <TableRow>
              <TableCell>Nombre</TableCell>
              <TableCell>Email</TableCell>
              <TableCell>Teléfono</TableCell>
              <TableCell>Identificador</TableCell>
              <TableCell align="center">Ubicación</TableCell>
              <TableCell align="center">Recolecciones</TableCell>
              <TableCell align="center">Estado</TableCell>
              <TableCell align="center">Acciones</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {contribuyentes.length === 0 ? (
              <TableRow>
                <TableCell colSpan={8} align="center">
                  <Typography variant="body2" color="text.secondary" py={3}>
                    No hay contribuyentes registrados
                  </Typography>
                </TableCell>
              </TableRow>
            ) : (
              contribuyentes.map((contribuyente) => (
                <TableRow key={contribuyente.Id} hover>
                  <TableCell>{contribuyente.Nombre || '-'}</TableCell>
                  <TableCell>{contribuyente.Email || '-'}</TableCell>
                  <TableCell>{contribuyente.Phone || '-'}</TableCell>
                  <TableCell>{contribuyente.Identificador || '-'}</TableCell>
                  <TableCell align="center">
                    {contribuyente.Latitud && contribuyente.Longitud ? (
                      <Chip
                        icon={<LocationIcon />}
                        label="Ubicado"
                        size="small"
                        color="primary"
                        variant="outlined"
                      />
                    ) : (
                      <Chip label="Sin ubicación" size="small" variant="outlined" />
                    )}
                  </TableCell>
                  <TableCell align="center">
                    <Tooltip title="Ver recolecciones">
                      <IconButton
                        size="small"
                        onClick={() => handleOpenRecoleccionesDialog(contribuyente)}
                        color={contribuyente.Qty_recolecciones > 0 ? 'primary' : 'default'}
                      >
                        <VisibilityIcon fontSize="small" />
                      </IconButton>
                    </Tooltip>
                    <Typography variant="caption" display="block">
                      {contribuyente.Qty_recolecciones || 0}
                    </Typography>
                  </TableCell>
                  <TableCell align="center">
                    {contribuyente.Activo === 1 ? (
                      <Chip
                        icon={<CheckCircleIcon />}
                        label="Activo"
                        size="small"
                        color="success"
                      />
                    ) : (
                      <Chip
                        icon={<CancelIcon />}
                        label="Inactivo"
                        size="small"
                        color="default"
                      />
                    )}
                  </TableCell>
                  <TableCell align="center">
                    <Tooltip title="Enviar mensaje">
                      <IconButton
                        color="secondary"
                        size="small"
                        onClick={() => handleOpenMensajesDialog(contribuyente)}
                      >
                        <MessageIcon fontSize="small" />
                      </IconButton>
                    </Tooltip>
                    <Tooltip title={contribuyente.Email ? 'Gestionar direcciones' : 'Sin email — no puede tener direcciones'}>
                      <span>
                        <IconButton
                          size="small"
                          color="info"
                          onClick={() => handleOpenDireccionesDialog(contribuyente)}
                          disabled={!contribuyente.Email}
                        >
                          <HomeIcon fontSize="small" />
                        </IconButton>
                      </span>
                    </Tooltip>
                    <Tooltip title="Editar">
                      <IconButton
                        color="primary"
                        size="small"
                        onClick={() => handleOpenDialog(contribuyente)}
                      >
                        <EditIcon fontSize="small" />
                      </IconButton>
                    </Tooltip>
                    <Tooltip title="Eliminar">
                      <IconButton
                        color="error"
                        size="small"
                        onClick={() => handleDeleteClick(contribuyente)}
                      >
                        <DeleteIcon fontSize="small" />
                      </IconButton>
                    </Tooltip>
                  </TableCell>
                </TableRow>
              ))
            )}
          </TableBody>
        </Table>
      </TableContainer>

      {/* Dialog para crear/editar contribuyente */}
      <Dialog open={openDialog} onClose={handleCloseDialog} maxWidth="md" fullWidth>
        <DialogTitle>
          {editingContribuyente ? 'Editar Contribuyente' : 'Nuevo Contribuyente'}
        </DialogTitle>
        <DialogContent>
          <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2, mt: 2 }}>
            {error && (
              <Alert severity="error" onClose={() => setError(null)}>
                {error}
              </Alert>
            )}

            <Grid container spacing={2}>
              <Grid item xs={12} sm={6}>
                <TextField
                  label="Nombre"
                  value={formData.Nombre}
                  onChange={(e) => handleChange('Nombre', e.target.value)}
                  required
                  fullWidth
                />
              </Grid>

              <Grid item xs={12} sm={6}>
                <TextField
                  label="Email"
                  type="email"
                  value={formData.Email}
                  onChange={(e) => handleChange('Email', e.target.value)}
                  fullWidth
                />
              </Grid>

              <Grid item xs={12} sm={6}>
                <TextField
                  label="Teléfono"
                  value={formData.Phone}
                  onChange={(e) => handleChange('Phone', e.target.value)}
                  fullWidth
                />
              </Grid>

              <Grid item xs={12} sm={6}>
                <TextField
                  label="Identificador"
                  value={formData.Identificador}
                  onChange={(e) => handleChange('Identificador', e.target.value)}
                  fullWidth
                />
              </Grid>

              <Grid item xs={12}>
                <Typography variant="subtitle2" gutterBottom>
                  Ubicación
                </Typography>
              </Grid>

              <Grid item xs={12} sm={5}>
                <TextField
                  label="Latitud"
                  type="number"
                  value={formData.Latitud}
                  onChange={(e) => handleChange('Latitud', e.target.value)}
                  inputProps={{ step: 'any' }}
                  fullWidth
                  size="small"
                />
              </Grid>

              <Grid item xs={12} sm={5}>
                <TextField
                  label="Longitud"
                  type="number"
                  value={formData.Longitud}
                  onChange={(e) => handleChange('Longitud', e.target.value)}
                  inputProps={{ step: 'any' }}
                  fullWidth
                  size="small"
                />
              </Grid>

              <Grid item xs={12} sm={2}>
                <IconButton
                  color="primary"
                  onClick={handleOpenMapPicker}
                  sx={{ mt: 0.5 }}
                >
                  <LocationIcon />
                </IconButton>
              </Grid>

              <Grid item xs={12}>
                <FormControlLabel
                  control={
                    <Switch
                      checked={formData.Activo === 1}
                      onChange={(e) => handleChange('Activo', e.target.checked ? 1 : 0)}
                    />
                  }
                  label="Activo"
                />
              </Grid>
            </Grid>
          </Box>
        </DialogContent>
        <DialogActions>
          <Button onClick={handleCloseDialog}>Cancelar</Button>
          <Button
            onClick={handleSubmit}
            variant="contained"
            disabled={createMutation.isPending || updateMutation.isPending}
          >
            {createMutation.isPending || updateMutation.isPending ? (
              <CircularProgress size={24} />
            ) : editingContribuyente ? (
              'Actualizar'
            ) : (
              'Crear'
            )}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Dialog para confirmar eliminación */}
      <Dialog open={deleteDialogOpen} onClose={handleCloseDeleteDialog}>
        <DialogTitle>Confirmar Eliminación</DialogTitle>
        <DialogContent>
          {deleteInfo && (
            <>
              {deleteInfo.canDelete ? (
                <Alert severity="info">{deleteInfo.message}</Alert>
              ) : (
                <Alert severity="warning">{deleteInfo.message}</Alert>
              )}
              {contribuyenteToDelete && (
                <Box sx={{ mt: 2 }}>
                  <Typography variant="body2">
                    <strong>Nombre:</strong> {contribuyenteToDelete.Nombre}
                  </Typography>
                  <Typography variant="body2">
                    <strong>Email:</strong> {contribuyenteToDelete.Email || '-'}
                  </Typography>
                </Box>
              )}
            </>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={handleCloseDeleteDialog}>Cancelar</Button>
          <Button
            onClick={handleConfirmDelete}
            color="error"
            variant="contained"
            disabled={!deleteInfo?.canDelete || deleteMutation.isPending}
          >
            {deleteMutation.isPending ? <CircularProgress size={24} /> : 'Eliminar'}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Dialog de mensajes */}
      <Dialog open={mensajesDialogOpen} onClose={handleCloseMensajesDialog} maxWidth="md" fullWidth>
        <DialogTitle>
          Mensajes — {mensajeContribuyente?.Nombre}
          {mensajesData?.tieneToken === false && (
            <Tooltip title="Sin dispositivo FCM registrado">
              <NoTokenIcon fontSize="small" color="warning" sx={{ ml: 1, verticalAlign: 'middle' }} />
            </Tooltip>
          )}
          {mensajeContribuyente?.Email && (
            <Typography variant="caption" display="block" color="text.secondary">
              {mensajeContribuyente.Email}
            </Typography>
          )}
        </DialogTitle>
        <DialogContent>
          {/* Formulario de envío */}
          <Box sx={{ mb: 3 }}>
            <Typography variant="subtitle2" gutterBottom>
              Nuevo mensaje
            </Typography>
            {mensajesData?.tieneToken === false && (
              <Alert severity="warning" sx={{ mb: 1 }}>
                Este contribuyente no tiene un dispositivo registrado. El mensaje se guardará pero no se enviará como notificación push.
              </Alert>
            )}
            <Grid container spacing={1}>
              <Grid item xs={12}>
                <TextField
                  fullWidth
                  size="small"
                  label="Título"
                  value={mensajeForm.titulo}
                  onChange={(e) => setMensajeForm((p) => ({ ...p, titulo: e.target.value }))}
                />
              </Grid>
              <Grid item xs={12}>
                <TextField
                  fullWidth
                  size="small"
                  label="Mensaje"
                  multiline
                  rows={2}
                  value={mensajeForm.mensaje}
                  onChange={(e) => setMensajeForm((p) => ({ ...p, mensaje: e.target.value }))}
                />
              </Grid>
              <Grid item xs={12} sx={{ display: 'flex', justifyContent: 'flex-end' }}>
                <Button
                  variant="contained"
                  startIcon={<SendIcon />}
                  onClick={handleEnviarMensaje}
                  disabled={enviarMensajeMutation.isPending}
                >
                  {enviarMensajeMutation.isPending ? 'Enviando...' : 'Enviar'}
                </Button>
              </Grid>
            </Grid>
          </Box>

          <Divider sx={{ mb: 2 }} />

          {/* Historial de mensajes */}
          <Typography variant="subtitle2" gutterBottom>
            Historial
          </Typography>
          {mensajesLoading ? (
            <Box display="flex" justifyContent="center" p={2}>
              <CircularProgress size={24} />
            </Box>
          ) : !mensajesData?.data?.length ? (
            <Typography variant="body2" color="text.secondary" align="center" py={2}>
              No hay mensajes enviados
            </Typography>
          ) : (
            <TableContainer component={Paper} variant="outlined">
              <Table size="small">
                <TableHead>
                  <TableRow>
                    <TableCell>Fecha</TableCell>
                    <TableCell>Título</TableCell>
                    <TableCell>Mensaje</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {mensajesData.data.map((msg) => (
                    <TableRow key={msg.Id}>
                      <TableCell sx={{ whiteSpace: 'nowrap' }}>
                        {formatFecha(msg.Fecha)}
                      </TableCell>
                      <TableCell>{msg.Titulo}</TableCell>
                      <TableCell>{msg.Mensaje}</TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </TableContainer>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={handleCloseMensajesDialog}>Cerrar</Button>
        </DialogActions>
      </Dialog>

      {/* Snackbar */}
      <Snackbar
        open={snackbar.open}
        autoHideDuration={5000}
        onClose={() => setSnackbar((s) => ({ ...s, open: false }))}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
      >
        <Alert
          onClose={() => setSnackbar((s) => ({ ...s, open: false }))}
          severity={snackbar.severity}
          sx={{ width: '100%' }}
        >
          {snackbar.message}
        </Alert>
      </Snackbar>

      {/* Map Picker (formulario principal) */}
      <MapLocationPicker
        open={mapPickerOpen}
        onClose={handleCloseMapPicker}
        onSelectLocation={handleLocationSelected}
        initialLat={formData.Latitud}
        initialLng={formData.Longitud}
        title="Seleccionar Ubicación del Contribuyente"
      />

      {/* Map Picker (formulario de nueva dirección) */}
      <MapLocationPicker
        open={mapPickerForDireccionOpen}
        onClose={() => setMapPickerForDireccionOpen(false)}
        onSelectLocation={(loc) => {
          setAddDireccionForm((prev) => ({ ...prev, latitud: loc.lat, longitud: loc.lng }));
          setAddDireccionErrors((prev) => { const e = { ...prev }; delete e.latitud; delete e.longitud; return e; });
          setMapPickerForDireccionOpen(false);
        }}
        initialLat={addDireccionForm.latitud}
        initialLng={addDireccionForm.longitud}
        title="Seleccionar ubicación de la dirección"
      />

      {/* Dialog: lista de direcciones del contribuyente */}
      <Dialog
        open={direccionesDialogOpen}
        onClose={handleCloseDireccionesDialog}
        maxWidth="md"
        fullWidth
      >
        <DialogTitle>
          Direcciones — {direccionesContribuyente?.Nombre}
          {direccionesContribuyente?.Email && (
            <Typography variant="caption" display="block" color="text.secondary">
              {direccionesContribuyente.Email}
            </Typography>
          )}
        </DialogTitle>
        <DialogContent>
          <Box sx={{ display: 'flex', justifyContent: 'flex-end', mb: 2 }}>
            <Button
              variant="contained"
              size="small"
              startIcon={<AddIcon />}
              onClick={handleOpenAddDireccion}
            >
              Agregar dirección
            </Button>
          </Box>

          {direccionesLoading ? (
            <Box display="flex" justifyContent="center" p={3}>
              <CircularProgress />
            </Box>
          ) : !direccionesData?.data?.length ? (
            <Typography variant="body2" color="text.secondary" align="center" py={3}>
              Este contribuyente no tiene direcciones registradas
            </Typography>
          ) : (
            <TableContainer component={Paper} variant="outlined">
              <Table size="small">
                <TableHead>
                  <TableRow>
                    <TableCell>Alias</TableCell>
                    <TableCell>Identificador</TableCell>
                    <TableCell>Latitud</TableCell>
                    <TableCell>Longitud</TableCell>
                    <TableCell>Registrada</TableCell>
                    <TableCell align="center">Acción</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {direccionesData.data.map((dir) => (
                    <TableRow key={dir.Id}>
                      <TableCell>{dir.Alias || '—'}</TableCell>
                      <TableCell>{dir.Identificador || '—'}</TableCell>
                      <TableCell>{dir.Latitud}</TableCell>
                      <TableCell>{dir.Longitud}</TableCell>
                      <TableCell>{formatFecha(dir.CreadoEn)}</TableCell>
                      <TableCell align="center">
                        <Tooltip title="Ver en mapa">
                          <IconButton
                            size="small"
                            color="primary"
                            onClick={() => {
                              setViewMapDireccion(dir);
                              setViewMapDireccionOpen(true);
                            }}
                          >
                            <LocationIcon fontSize="small" />
                          </IconButton>
                        </Tooltip>
                        <Tooltip title="Editar dirección">
                          <IconButton
                            size="small"
                            color="info"
                            onClick={() => handleOpenEditDireccion(dir)}
                          >
                            <EditIcon fontSize="small" />
                          </IconButton>
                        </Tooltip>
                        <Tooltip title="Eliminar dirección">
                          <IconButton
                            size="small"
                            color="error"
                            disabled={desactivarDireccionMutation.isPending}
                            onClick={() =>
                              desactivarDireccionMutation.mutate({
                                email: direccionesContribuyente.Email,
                                idDireccion: dir.Id,
                              })
                            }
                          >
                            <DeleteOutlineIcon fontSize="small" />
                          </IconButton>
                        </Tooltip>
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </TableContainer>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={handleCloseDireccionesDialog}>Cerrar</Button>
        </DialogActions>
      </Dialog>

      {/* Map Picker (ver ubicación de dirección — solo lectura) */}
      <MapLocationPicker
        open={viewMapDireccionOpen}
        onClose={() => { setViewMapDireccionOpen(false); setViewMapDireccion(null); }}
        onSelectLocation={() => {}}
        initialLat={viewMapDireccion?.Latitud}
        initialLng={viewMapDireccion?.Longitud}
        title={viewMapDireccion ? `Ubicación — ${viewMapDireccion.Alias}` : 'Ubicación'}
        readOnly
      />

      {/* Dialog: editar dirección existente */}
      <Dialog
        open={editDireccionDialogOpen}
        onClose={handleCloseEditDireccion}
        maxWidth="sm"
        fullWidth
      >
        <DialogTitle>Editar dirección — {editingDireccion?.Alias}</DialogTitle>
        <DialogContent>
          <Grid container spacing={2} sx={{ mt: 1 }}>
            <Grid item xs={12}>
              <TextField
                fullWidth
                label="Alias *"
                value={editDireccionForm.alias}
                onChange={(e) => {
                  setEditDireccionForm((p) => ({ ...p, alias: e.target.value }));
                  setEditDireccionErrors((p) => { const e = { ...p }; delete e.alias; return e; });
                }}
                error={!!editDireccionErrors.alias}
                helperText={editDireccionErrors.alias}
              />
            </Grid>
            <Grid item xs={12}>
              <TextField
                fullWidth
                label="Identificador"
                value={editDireccionForm.identificador}
                onChange={(e) => setEditDireccionForm((p) => ({ ...p, identificador: e.target.value }))}
              />
            </Grid>
            <Grid item xs={12}>
              <Typography variant="subtitle2" gutterBottom>
                Ubicación
              </Typography>
            </Grid>
            <Grid item xs={5}>
              <TextField
                fullWidth
                label="Latitud *"
                type="number"
                value={editDireccionForm.latitud}
                onChange={(e) => {
                  setEditDireccionForm((p) => ({ ...p, latitud: e.target.value }));
                  setEditDireccionErrors((p) => { const e = { ...p }; delete e.latitud; return e; });
                }}
                inputProps={{ step: 'any' }}
                size="small"
                error={!!editDireccionErrors.latitud}
                helperText={editDireccionErrors.latitud}
              />
            </Grid>
            <Grid item xs={5}>
              <TextField
                fullWidth
                label="Longitud *"
                type="number"
                value={editDireccionForm.longitud}
                onChange={(e) => {
                  setEditDireccionForm((p) => ({ ...p, longitud: e.target.value }));
                  setEditDireccionErrors((p) => { const e = { ...p }; delete e.longitud; return e; });
                }}
                inputProps={{ step: 'any' }}
                size="small"
                error={!!editDireccionErrors.longitud}
                helperText={editDireccionErrors.longitud}
              />
            </Grid>
            <Grid item xs={2}>
              <IconButton
                color="primary"
                onClick={() => setMapPickerForEditOpen(true)}
                sx={{ mt: 0.5 }}
              >
                <LocationIcon />
              </IconButton>
            </Grid>
            {actualizarDireccionMutation.isError && (
              <Grid item xs={12}>
                <Alert severity="warning">
                  {actualizarDireccionMutation.error?.response?.data?.error?.message || 'Error al actualizar la dirección'}
                </Alert>
              </Grid>
            )}
          </Grid>
        </DialogContent>
        <DialogActions>
          <Button onClick={handleCloseEditDireccion} startIcon={<CancelIcon />}>
            Cancelar
          </Button>
          <Button
            onClick={handleSubmitEditDireccion}
            variant="contained"
            disabled={actualizarDireccionMutation.isPending}
          >
            {actualizarDireccionMutation.isPending ? <CircularProgress size={24} /> : 'Guardar'}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Map Picker (editar coordenadas de dirección existente) */}
      <MapLocationPicker
        open={mapPickerForEditOpen}
        onClose={() => setMapPickerForEditOpen(false)}
        onSelectLocation={(loc) => {
          setEditDireccionForm((prev) => ({ ...prev, latitud: loc.lat, longitud: loc.lng }));
          setEditDireccionErrors((prev) => { const e = { ...prev }; delete e.latitud; delete e.longitud; return e; });
          setMapPickerForEditOpen(false);
        }}
        initialLat={editDireccionForm.latitud}
        initialLng={editDireccionForm.longitud}
        title="Seleccionar nueva ubicación"
      />

      {/* Dialog: agregar nueva dirección */}
      <Dialog
        open={addDireccionDialogOpen}
        onClose={handleCloseAddDireccion}
        maxWidth="sm"
        fullWidth
      >
        <DialogTitle>Nueva dirección — {direccionesContribuyente?.Nombre}</DialogTitle>
        <DialogContent>
          <Grid container spacing={2} sx={{ mt: 1 }}>
            <Grid item xs={12}>
              <TextField
                fullWidth
                label="Alias *"
                placeholder="Ej: Casa, Trabajo, Depósito..."
                value={addDireccionForm.alias}
                onChange={(e) => {
                  setAddDireccionForm((p) => ({ ...p, alias: e.target.value }));
                  setAddDireccionErrors((p) => { const e = { ...p }; delete e.alias; return e; });
                }}
                error={!!addDireccionErrors.alias}
                helperText={addDireccionErrors.alias}
              />
            </Grid>
            <Grid item xs={12}>
              <TextField
                fullWidth
                label="Identificador"
                placeholder="Ej: 12345678"
                value={addDireccionForm.identificador}
                onChange={(e) => setAddDireccionForm((p) => ({ ...p, identificador: e.target.value }))}
              />
            </Grid>
            <Grid item xs={12}>
              <Typography variant="subtitle2" gutterBottom>
                Ubicación
              </Typography>
            </Grid>
            <Grid item xs={5}>
              <TextField
                fullWidth
                label="Latitud *"
                type="number"
                value={addDireccionForm.latitud}
                onChange={(e) => {
                  setAddDireccionForm((p) => ({ ...p, latitud: e.target.value }));
                  setAddDireccionErrors((p) => { const e = { ...p }; delete e.latitud; return e; });
                }}
                inputProps={{ step: 'any' }}
                size="small"
                error={!!addDireccionErrors.latitud}
                helperText={addDireccionErrors.latitud}
              />
            </Grid>
            <Grid item xs={5}>
              <TextField
                fullWidth
                label="Longitud *"
                type="number"
                value={addDireccionForm.longitud}
                onChange={(e) => {
                  setAddDireccionForm((p) => ({ ...p, longitud: e.target.value }));
                  setAddDireccionErrors((p) => { const e = { ...p }; delete e.longitud; return e; });
                }}
                inputProps={{ step: 'any' }}
                size="small"
                error={!!addDireccionErrors.longitud}
                helperText={addDireccionErrors.longitud}
              />
            </Grid>
            <Grid item xs={2}>
              <IconButton
                color="primary"
                onClick={() => setMapPickerForDireccionOpen(true)}
                sx={{ mt: 0.5 }}
              >
                <LocationIcon />
              </IconButton>
            </Grid>
            {agregarDireccionMutation.isError && (
              <Grid item xs={12}>
                <Alert severity="warning">
                  Las coordenadas deben estar dentro de una zona de servicio configurada.
                </Alert>
              </Grid>
            )}
          </Grid>
        </DialogContent>
        <DialogActions>
          <Button onClick={handleCloseAddDireccion} startIcon={<CancelIcon />}>
            Cancelar
          </Button>
          <Button
            onClick={handleSubmitDireccion}
            variant="contained"
            disabled={agregarDireccionMutation.isPending}
          >
            {agregarDireccionMutation.isPending ? <CircularProgress size={24} /> : 'Agregar'}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Dialog para ver recolecciones */}
      <Dialog open={recoleccionesDialogOpen} onClose={handleCloseRecoleccionesDialog} maxWidth="lg" fullWidth>
        <DialogTitle>
          Recolecciones de {selectedContribuyente?.Nombre}
          {selectedContribuyente?.Email && (
            <Typography variant="caption" display="block" color="text.secondary">
              {selectedContribuyente.Email}
            </Typography>
          )}
        </DialogTitle>
        <DialogContent>
          {recoleccionesLoading ? (
            <Box display="flex" justifyContent="center" p={3}>
              <CircularProgress />
            </Box>
          ) : (
            <TableContainer>
              <Table size="small">
                <TableHead>
                  <TableRow>
                    <TableCell>Fecha Pedido</TableCell>
                    <TableCell>Zona</TableCell>
                    <TableCell>Servicio</TableCell>
                    <TableCell>Volumen</TableCell>
                    <TableCell>Estado</TableCell>
                    <TableCell>Fecha Retiro</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {!recoleccionesData?.data || recoleccionesData.data.length === 0 ? (
                    <TableRow>
                      <TableCell colSpan={6} align="center">
                        <Typography variant="body2" color="text.secondary" py={2}>
                          No hay recolecciones registradas para este contribuyente
                        </Typography>
                      </TableCell>
                    </TableRow>
                  ) : (
                    recoleccionesData.data.map((recoleccion) => (
                      <TableRow key={recoleccion.Id} hover>
                        <TableCell>{formatFecha(recoleccion.FechaPedido)}</TableCell>
                        <TableCell>{recoleccion.Zona}</TableCell>
                        <TableCell>{recoleccion.Servicio}</TableCell>
                        <TableCell>{recoleccion.Volumen} m³</TableCell>
                        <TableCell>
                          {recoleccion.FechaRetiro ? (
                            <Chip label="Retirado" size="small" color="success" />
                          ) : recoleccion.FechaAnulado ? (
                            <Chip label="Anulado" size="small" color="error" />
                          ) : recoleccion.FechaObservado ? (
                            <Chip label="Observado" size="small" color="warning" />
                          ) : (
                            <Chip label="Pendiente" size="small" color="default" />
                          )}
                        </TableCell>
                        <TableCell>{formatFecha(recoleccion.FechaRetiro)}</TableCell>
                      </TableRow>
                    ))
                  )}
                </TableBody>
              </Table>
            </TableContainer>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={handleCloseRecoleccionesDialog}>Cerrar</Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default Contribuyentes;
